import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	Anchor,
	ContextMenu,
	ExpandButton,
	ExpandableText,
	FormattedText,
	FormattedTextReplacement,
	IconButton,
	Intersection,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import { HubAvatar } from "../../HubAvatar/HubAvatar";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import {
	HubCommentMediaKind,
	HubCommentModel,
	ReactionKind,
	UserMentionViewModel
} from "client/Api/Hub/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import styles from "./Comment.module.less";
import {
	generateTrackingId,
	isNotNil,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import classNames from "classnames";
import { NameAndDate } from "./NameAndDate";
import { EditForm } from "./EditForm";
import { CommentFooter } from "./CommentFooter";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { MobileCommentMode } from "../MobileComment/MobileComment";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { HubCommentSectionService } from "../HubCommentSection";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { FindUrlResult, findUrlsInText } from "client/Misc/findUrlsInText";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { HubUserInfoPopOver } from "../../HubUserInfoPopOver/HubUserInfoPopOver";

export type CommentProps = {
	comment: HubCommentModel;
	onUpdated: (comment: HubCommentModel) => void;
	onDeleted: (commentId: number) => void;
	mobileMode: MobileCommentMode;
	onMobileCommentModeChange: (newValue: MobileCommentMode) => void;
	onCreated: (comment: HubCommentModel) => void;
	isMobile: boolean;
	onReactionChange: (
		newReaction: ReactionKind,
		commentId: number
	) => void;
	replies?: readonly HubCommentModel[];
	commentIdToNavigateTo?: number;
	resetNavigateCommentId: () => void;
	currentUserInfo: UserIdentityModel;
	service: HubCommentSectionService;
	timezone: TimeZone;
	onViewImage: (imageUrl: string) => void;
	options: HubCommentOptionsModel;
};

type CommentActionsContext = {
	canUserEditComment: boolean;
	canUserDeleteComment: boolean;
};

export const Comment = (props: CommentProps): ReactElement => {
	const {
		comment,
		onUpdated,
		onDeleted,
		onMobileCommentModeChange,
		onCreated,
		isMobile,
		onReactionChange,
		replies,
		commentIdToNavigateTo,
		resetNavigateCommentId,
		mobileMode,
		currentUserInfo,
		service,
		timezone,
		onViewImage,
		options
	} = props;

	const {
		id,
		message,
		mentions,
		userInfo,
		parentCommentId,
		publicationTime,
		currentUserCanEdit,
		currentUserCanDelete,
		statistics,
		media
	} = comment;

	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(moreButtonRef);
	const [showReplies, setShowReplies] = useState<boolean>(false);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const alertManager: AlertManager = useAlerts();

	const hasActions: boolean = currentUserCanEdit
		|| currentUserCanDelete;

	const deleteCommentHandler = useCallback(
		async (commentId: number): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "delete-comment-confirmation-dialog",
				title: localize("_AreYouSureYouWantToDeleteThisComment"),
				confirmText: localize("_Delete")
			});

			if (!result.wasSuccessful) {
				return;
			}
			try {
				await service.deleteComment(commentId, globalCancellation);

				onDeleted(commentId);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			openConfirmationDialog,
			onDeleted,
			service,
			globalCancellation
		]
	);

	const actions = useMemo(
		(): ReadonlyArray<Action<CommentActionsContext>> => ([
			{
				kind: ActionKind.Regular,
				name: "Edit",
				title: localize("_Edit"),
				icon: "pen",
				canExecute: (context) => context.canUserEditComment,
				execute: _ => {
					if (isMobile) {
						onMobileCommentModeChange({
							kind: "edit",
							commentId: id,
							message,
							mentions,
							authorUserInfo: userInfo,
							media,
							header: userInfo.userId === currentUserInfo.userId
								? localize("_EditYourComment")
								: resolveFormattedTextString({
									template: localize("_EditCommentOfName"),
									replacements: [
										{
											what: "{{Name}}",
											filler: getComputedDisplayName(userInfo).computedDisplayName
										}
									]
								})
						});
						return;
					}

					setIsEditMode(true);
				}
			},
			{
				kind: ActionKind.Regular,
				name: "Delete",
				title: localize("_Delete"),
				icon: "trash-can",
				canExecute: (context) => context.canUserDeleteComment,
				execute: _ => deleteCommentHandler(id)
			}
		]),
		[
			id,
			isMobile,
			media,
			mentions,
			message,
			onMobileCommentModeChange,
			userInfo,
			deleteCommentHandler,
			currentUserInfo.userId
		]
	);

	const context = useMemo(
		(): CommentActionsContext => ({
			canUserEditComment: currentUserCanEdit,
			canUserDeleteComment: currentUserCanDelete
		}),
		[currentUserCanDelete, currentUserCanEdit]
	);

	const saveCommentHandler = (comment: HubCommentModel): void => {
		onUpdated(comment);
		setIsEditMode(false);
	};

	const toggleShowReplies = (): void => {
		setShowReplies(prevState => !prevState);
	};

	useEffect(
		() => {
			if (!commentIdToNavigateTo) {
				return;
			}

			if (id === commentIdToNavigateTo) {
				// For w/e reason if we don't delay the scrolling, some browser will scroll to far
				// and the comment goes "under" the header
				setTimeout(
					(): void => {
						ref.current
							?.scrollIntoView({
								behavior: "smooth",
								block: "nearest"
							});
					},
					100
				);

				resetNavigateCommentId();

				return;
			}

			const containsCommentId = Boolean(replies?.find((reply) => reply.id === commentIdToNavigateTo));

			if (containsCommentId) {
				setShowReplies(true);
			}
		},
		[
			id,
			commentIdToNavigateTo,
			replies,
			resetNavigateCommentId
		]
	);

	const isEditingOrReplyingOnMobile = (mobileMode.kind === "edit" && mobileMode.commentId === id)
		|| (mobileMode.kind === "reply" && mobileMode.replyCommentId === id);

	let toggleButtonText: string;

	if (showReplies) {
		if ((replies?.length ?? 0) > 1) {
			toggleButtonText = localize("_HideNReplies");
		} else {
			toggleButtonText = localize("_Hide1Reply");
		}
	} else {
		if ((replies?.length ?? 0) > 1) {
			toggleButtonText = localize("_ShowNReplies");
		} else {
			toggleButtonText = localize("_Show1Reply");
		}
	}

	const replyMobileCommentHandler = useCallback(
		(
			parentCommentId: number,
			replyCommentId: number,
			authorUserInfo: UserIdentityModel
		): void => {
			onMobileCommentModeChange({
				kind: "reply",
				authorUserInfo,
				parentCommentId,
				replyCommentId,
				header: authorUserInfo.userId === currentUserInfo.userId
					? localize("_ReplyToYourOwnComment")
					: resolveFormattedTextString({
						template: localize("_ReplyToTheCommentOfName"),
						replacements: [
							{
								what: "{{Name}}",
								filler: getComputedDisplayName(authorUserInfo).computedDisplayName
							}
						]
					})
			});
		},
		[onMobileCommentModeChange, currentUserInfo]
	);

	const imgUrl = useMemo(
		(): string | undefined => {
			return media?.mediaKind === HubCommentMediaKind.Image
				? service.getCommentImageUrl(media.imageId)
				: undefined;
		},
		[media, service]
	);

	const urlPositions = useMemo(
		(): readonly FindUrlResult[] => {
			return findUrlPositions(message, mentions);
		},
		[mentions, message]
	);

	const renderMentionReplacements = (): readonly FormattedTextReplacement[] => {
		return mentions.map(
			(x) => ({
				from: x.from,
				to: x.to,
				filler: (
					<Mention user={x.user} />
				)
			})
		);
	};

	const renderLinkReplacements = (): readonly FormattedTextReplacement[] => {
		return urlPositions.map(
			x => ({
				from: x.start,
				to: x.end,
				filler: (
					<Anchor
						href={x.url}
						target="_blank"
						className={styles.link}
					>
						{x.matchedText}
					</Anchor>
				)
			})
		);
	};

	return (
		<div
			{...setTrackingId(isNotNil(parentCommentId) ? "reply-comment" : "comment")}
			className={styles.root}
			ref={ref}
		>
			<HubAvatar
				authorUserInfo={userInfo}
				size="small"
				shouldShowContactDetails
			/>
			<div className={styles.commentWithReactivityActionsContainer}>
				<div
					className={classNames(
						styles.commentBox,
						{
							[styles.commentBox_isEditingOnMobile]: isMobile && isEditingOrReplyingOnMobile
						}
					)}
				>
					<div className={styles.content}>
						<NameAndDate
							authorUserInfo={userInfo}
							publicationTime={parseDtoDate(publicationTime)}
							timezone={timezone}
						/>
						{isEditMode
							? (
								<EditForm
									commentId={id}
									message={message}
									mentions={mentions}
									onSave={saveCommentHandler}
									onCancel={() => setIsEditMode(false)}
									service={service}
									media={media}
									commentOptions={options}
								/>
							)
							: (
								<div className={styles.messageWithImage}>
									<ExpandableText
										trackingId="message"
										text={(
											<FormattedText
												text={message}
												thingsToReplace={
													[
														...renderMentionReplacements(),
														...renderLinkReplacements()
													]
												}
											/>
										)}
									/>
									{isNotNil(imgUrl) && (
										<Image
											onViewImage={onViewImage}
											imgUrl={imgUrl}
										/>
									)}
								</div>
							)
						}
					</div>
					{hasActions && !isEditMode && (
						<div className={styles.actions}>
							<IconButton
								trackingId="more"
								icon="ellipsis"
								kind="transparent"
								onClick={e => contextMenuHandler({ x: e.clientX, y: e.clientY })}
								ref={moreButtonRef}
								shape="round"
								isActive={isNotNil(contextMenuProps)}
							/>
							{contextMenuProps && (
								<ContextMenu
									{...contextMenuProps}
									actions={actions}
									context={context}
									trackingId="comment-more-actions"
									title={localize("_Comment")}
								/>
							)}
						</div>
					)}
				</div>
				<CommentFooter
					commentId={id}
					parentId={parentCommentId}
					onReactionChange={onReactionChange}
					statistics={statistics}
					authorUserInfo={userInfo}
					onReplyMobileComment={replyMobileCommentHandler}
					onReplyCreated={onCreated}
					currentUserInfo={currentUserInfo}
					service={service}
					options={options}
				/>
				{
					Boolean(replies?.length) && (
						<div className={styles.expandButton}>
							<ExpandButton
								trackingId="expand-replies"
								isExpanded={showReplies}
								text={(
									<FormattedText
										text={toggleButtonText}
										thingsToReplace={[
											{
												what: "{{RepliesCount}}",
												filler: `${replies?.length}`
											}
										]}
									/>
								)}
								onClick={toggleShowReplies}
								size="small"
							/>
						</div>
					)
				}
				{
					showReplies && (
						<div
							{...setTrackingId("reply-list")}
							className={styles.replies}
						>
							{replies?.map((reply) => (
								<Comment
									key={reply.id}
									comment={reply}
									onUpdated={onUpdated}
									onDeleted={onDeleted}
									isMobile={isMobile}
									mobileMode={mobileMode}
									onReactionChange={onReactionChange}
									commentIdToNavigateTo={commentIdToNavigateTo}
									resetNavigateCommentId={resetNavigateCommentId}
									onCreated={onCreated}
									currentUserInfo={currentUserInfo}
									onMobileCommentModeChange={onMobileCommentModeChange}
									service={service}
									timezone={timezone}
									onViewImage={onViewImage}
									options={options}
								/>
							))}
						</div>
					)
				}
			</div>
			{confirmationDialog}
		</div>
	);
};

Comment.displayName = "Comment";

type MentionProps = {
	user: UserIdentityModel;
};

const Mention = (props: MentionProps): ReactElement => {
	const {
		user
	} = props;

	const displayName = useMemo(
		(): string => {
			return getComputedDisplayName({
				firstName: user.firstName,
				lastName: user.lastName
			})
				.computedDisplayName;
		},
		[user.firstName, user.lastName]
	);

	return (
		<span className={styles.mention}>
			<HubUserInfoPopOver
				trackingId={generateTrackingId("user-mention", `|${user.userId}`)}
				userIdentity={user}
				contentAlignment="tl"
				targetAlignment="bl"
			>
				{displayName}
			</HubUserInfoPopOver>
		</span>
	);
};
Mention.displayName = "Mention";

type ImageProps = {
	onViewImage: (imageUrl: string) => void;
	imgUrl: string;
};

const Image = (props: ImageProps): ReactElement => {
	const { onViewImage, imgUrl } = props;

	const [shouldLoadImage, setShouldLoadImage] = useState<boolean>(false);

	return (
		<Intersection
			onEnter={() => setShouldLoadImage(true)}
			runOnce
		>
			<Anchor
				className={styles.imageContainer}
				onClick={() => onViewImage(imgUrl)}
			>
				{shouldLoadImage && (
					<img
						src={imgUrl}
						className={styles.image}
					/>
				)}
			</Anchor>
		</Intersection>
	);
};
Image.displayName = "Image";

function findUrlPositions(
	message: string,
	mentions: readonly UserMentionViewModel[]
): readonly FindUrlResult[] {
	const sortedMentions: UserMentionViewModel[] = sortBy(mentions, m => m.from);

	const result: FindUrlResult[] = [];
	let offset: number = 0;

	for (const mention of sortedMentions) {
		const textStart: number = offset;
		const textEnd: number = mention.from;

		if (textEnd === textStart) {
			offset = mention.to;
			continue;
		}

		if (textEnd < textStart) {
			throw new Error("Incorrect mention positions, probably overlap.");
		}

		const text: string = message.substring(textStart, textEnd);

		const urls: readonly FindUrlResult[] = findUrlsInText(
			text,
			{
				shouldMatchOnWww: true,
				globalPositionOffset: offset
			}
		);

		result.push(...urls);

		offset = mention.to;
	}

	if (offset < message.length) {
		const text: string = message.substring(offset, message.length);

		const urls: readonly FindUrlResult[] = findUrlsInText(
			text,
			{
				shouldMatchOnWww: true,
				globalPositionOffset: offset
			}
		);

		result.push(...urls);
	}

	return result;
}